import {action, makeObservable, observable} from "mobx";
import {OrbitUserFlowAnalyticsCategory} from "../analytics/analytics.service.interface";
import {MenuItemKey} from "../configurations/config-data-sections/generated-config-const";
import {IMenuItemActionFactoryService} from "../menu-item-action-factory/menu-item-action-factory.service.interface";
import {loadCustomMenuItemsRenderers} from "../menu-items-common/renderer/dynamic-custom-menu-item-renderers-loader";
import {IMenuItemRenderer} from "../menu-items-common/renderer/menu-item.renderer.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {ServiceBase} from "../ServiceBase";
import {GenericTabsNavigationItemRenderer} from "./menu-items/generic-tabs-navigation-item.renderer";
import {TabsNavigationItemConfigReader} from "./menu-items/tabs-navigation-item-config-reader";
import {ITabsNavigationService} from "./tabs-navigation.interface";
import {ARENA_GROUPS_ORDER_BY_ORIENTATION} from "../arenas/arena-consts";
import {ARENA_IDS} from "../arenas/arena-ids.enum";

export class TabsNavigationService extends ServiceBase implements ITabsNavigationService {
    constructor(services: IServiceFactoryExtended,
        private readonly menuItemActionFactory: IMenuItemActionFactoryService) {
        super(services);
        makeObservable(this);

        this._menuItemConfigReader = new TabsNavigationItemConfigReader(services);

        // When custom renderers will add - uncomment beneath lines to load them:
        // let rendererModules = require.context('./custom-renderers', true, /.+\.renderer\.(ts|tsx)$/);
        // this._customMenuItems = loadCustomMenuItemsRenderers(rendererModules.keys().map(rendererModules), this._menuItemConfigReader, this.services, this.menuItemActionFactory);

        this._customMenuItems = loadCustomMenuItemsRenderers([], this._menuItemConfigReader, this.services, this.menuItemActionFactory);

        this._setMenuLinksBlockOrder(this.services.configuration.data.tabsNavigation.menuLinksBlockOrder);

        this.reaction(() => this.services.userGameOrientation.gameGroup,
            (group) => {
                if (group?.groupId) {
                    this._orderMenuLinksBlockOrder(group.groupId)
                }
            }, {
            fireImmediately: true
        });
    }

    private readonly _menuItemConfigReader: TabsNavigationItemConfigReader;
    private readonly _customMenuItems: Record<MenuItemKey, IMenuItemRenderer>;

    public getMenuItem(key: MenuItemKey): IMenuItemRenderer {
        let menuItemRenderer = this.services.regulation.current.menuItems.getTabsNavigationItem(key, this._menuItemConfigReader, this.menuItemActionFactory);
        if (menuItemRenderer) {
            return menuItemRenderer;
        }

        menuItemRenderer = this._customMenuItems[key];
        if (menuItemRenderer) {
            return menuItemRenderer;
        }

        return new GenericTabsNavigationItemRenderer(key, this._menuItemConfigReader, this.services, this.menuItemActionFactory);
    }


    @observable.ref
    _menuLinksBlockOrder: MenuItemKey[][] = [];

    public get menuLinksBlockOrder() {
        return this._menuLinksBlockOrder;
    }

    @action.bound
    private _setMenuLinksBlockOrder(arenas: MenuItemKey[][]): void {
        this._menuLinksBlockOrder = arenas;
    }

    private _orderMenuLinksBlockOrder(orientedGameGroupId: number): void {
        // Get suggestion arenas list for user orientation
        const suggestedOrder = ARENA_GROUPS_ORDER_BY_ORIENTATION[orientedGameGroupId];
        if (!suggestedOrder) {
            this.services.logger.error(`User orientation id ${orientedGameGroupId} is missing from mapping.`);
            return;
        }

        // Find the menu links block we need to reorder
        const originalMenuLinksBlockOrder = this.menuLinksBlockOrder;
        const menuLinksBlockToReorderIndex = originalMenuLinksBlockOrder.findIndex(menuArray =>
            menuArray.some(menuItem => suggestedOrder.includes(menuItem))
        );

        if (menuLinksBlockToReorderIndex === -1) {
            this.services.logger.error('No menu array found containing suggested arena items');
            return;
        }

        const arrayBlockToReorder = originalMenuLinksBlockOrder[menuLinksBlockToReorderIndex];

        // Check if the reorder menu link block contain homeMenuItem (to keep him first on the list)
        const hasHomeMenuItem = arrayBlockToReorder.includes(ARENA_IDS.HOME);

        // For some regulations we might get a suggested arena that isn't exist on the menu link blocks
        // so we need to filter out the arenas shouldn't be shown.
        const suggestedItemsShouldBeInNewOrder = suggestedOrder.filter(item =>
            arrayBlockToReorder.includes(item) && item !== ARENA_IDS.HOME
        );

        // We might have arenas that need to be on the list, but weren't in the suggested list
        const itemsThatWereNotSuggested = arrayBlockToReorder.filter(item =>
            !suggestedOrder.includes(item) && item !== ARENA_IDS.HOME
        );

        // Merge the suggestions and the arenas from config to the new order list
        const reorderedArenaArray = [
            ...(hasHomeMenuItem ? [ARENA_IDS.HOME] : []),
            ...suggestedItemsShouldBeInNewOrder,
            ...itemsThatWereNotSuggested
        ];

        // Reorder the original menu links blocks with the suggested arenas etc.
        const newMenuLinksBlockOrder = originalMenuLinksBlockOrder.map((array, index) =>
            index === menuLinksBlockToReorderIndex ? reorderedArenaArray : [...array]
        );

        this._setMenuLinksBlockOrder(newMenuLinksBlockOrder);
    }


    get shouldRenderTabsNavigation(): boolean {
        if (!this.services.mediaQuery.lAndBelow) {
            return false;
        }

        return this.services.abTestingFeatureDataProvider.siteNewNavigation.isEnabled;
    }

    public sendOrbitUserFlowAnalytics(tabOriginalName: string, label: string): void {
        const action = 'Click_' + tabOriginalName;
        this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.TabsNavigation, action, label);
    }
}
